import validations from "helpers/validations";
import { Offer, SavingsSummary } from "./types";

export const OFFER_TYPE_MAPPING: Record<string, string> = {
  internal_amber_schema: "Internal Amber Scheme",
};

// Sub-category labels for other_amber_offers
export const AMBER_OFFER_SUB_CATEGORY_LABELS: Record<string, string> = {
  loyalty_bonus: "Rebooker Offer",
  app_only: "App Only Offer",
  referral: "Referral Offer",
  group_booking: "Group Booking",
  vas_offer: "Bonus Deal",
};

export const getOfferTypeLabel = (type: string): string => {
  if (!type) return "";
  return OFFER_TYPE_MAPPING[type] || type;
};

/**
 * Get display label for an offer based on its type and sub_category
 * - For other_amber_offers: use sub_category from meta object (Rebooker Offer, App Only Offer, etc.)
 * - For vas_offer: always show "Bonus Deal"
 * - For property_offer: no label prefix
 */
export const getOfferDisplayLabel = (offer: Offer): string => {
  const { type, action_type } = offer;
  const subCategory = offer?.sub_category || "";

  // For other_amber_offers, check sub_category from meta or infer from action_type/name
  if (type === "other_amber_offers") {
    if (subCategory && AMBER_OFFER_SUB_CATEGORY_LABELS[subCategory]) {
      return AMBER_OFFER_SUB_CATEGORY_LABELS[subCategory];
    }

    // Fallback: infer from action_type or offer name
    if (action_type?.toLowerCase() === "install_app") {
      return "App Only Offer";
    }
  }
  // if (type === "property_offer" || type === "amber_exclusive_property_offer") {
  //   return "Property Offer";
  // }

  return "";
};

/**
 * Calculate days until expiry from end_date
 * Returns null if no end_date or already expired
 */
export const getDaysUntilExpiry = (endDate: string | null | undefined): number | null => {
  if (!endDate) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const expiry = new Date(endDate);
  expiry.setHours(0, 0, 0, 0);

  const diffTime = expiry.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays <= 7 && diffDays >= 0 ? diffDays : null;
};

/**
 * Format expiry text based on days remaining
 * - <= 7 days: "Expires in X days" (red)
 * - > 7 days: "Expires in X days" (blue)
 */
export const getExpiryText = (
  endDate: string | null | undefined,
): { text: string; isUrgent: boolean } | null => {
  const daysLeft = getDaysUntilExpiry(endDate);
  if (daysLeft === null) return null;

  if (daysLeft === 0) {
    return { text: "Expires today", isUrgent: true };
  }

  if (daysLeft === 1) {
    return { text: "Expires tomorrow", isUrgent: true };
  }

  return {
    text: `Expires in ${daysLeft} days`,
    isUrgent: daysLeft <= 7,
  };
};

/**
 * Calculate combined savings from all offers
 * - cashbackTotal: sum of property_offer and other_amber_offers with numeric values
 * - extraBenefitsTotal: sum of vas_offer values (or estimated from description)
 */
export const calculateSavingsSummary = (offers: Offer[]): SavingsSummary => {
  let cashbackTotal = 0;
  let extraBenefitsTotal = 0;

  offers?.forEach((offer) => {
    const value = typeof offer.value === "number" ? offer.value : 0;
    if (!(offer.type === "other_amber_offers" && offer.sub_category === "vas_offer")) {
      cashbackTotal += value;
    } else if (offer.type === "other_amber_offers" && offer.sub_category === "vas_offer") {
      extraBenefitsTotal += value;
    }
  });

  return { cashbackTotal, extraBenefitsTotal };
};

/**
 * Which parts of the desktop header savings tag to show (P1D-5457). Same rules as the banner:
 * the saving from `savingThreshold` up, the perks (extra benefits) part whenever above 0.
 */
export const getSavingsTagParts = (summary: SavingsSummary, savingThreshold: number) => ({
  showSaving: summary.cashbackTotal >= savingThreshold,
  showPerks: summary.extraBenefitsTotal > 0,
});

// Tolerates a missing list. `OffersSection` calls this during its render, and since the PDP now
// mounts while it is still loading (for the search → PDP view transition), `unsortedOffers` is
// `undefined` on that first pass — spreading it threw and took the whole page down.
export const getSortedOffers = (offer: any) =>
  [...(Array.isArray(offer) ? offer : [])].sort((a, b) => {
    // First, sort by type priority
    const typePriority: Record<string, number> = {
      property_offer: 1,
      amber_exclusive_property_offer: 2,
      amber_cashback_offer: 3,
      other_amber_offers: 4,
    };

    const priorityA = typePriority[a.type] || 999;
    const priorityB = typePriority[b.type] || 999;

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    const valueA = typeof a.value === "number" ? a.value : 0;
    const valueB = typeof b.value === "number" ? b.value : 0;

    // Within other_amber_offers: vas_offer first, the rest next, and non-monetary offers
    // (e.g. student essentials) last (P1D-5562)
    if (a.type === "other_amber_offers" && b.type === "other_amber_offers") {
      const rank = (o: any, value: number) => {
        if (value <= 0) return 2;
        return o.sub_category === "vas_offer" ? 0 : 1;
      };
      const rankA = rank(a, valueA);
      const rankB = rank(b, valueB);
      if (rankA !== rankB) {
        return rankA - rankB;
      }
    }

    // If types are the same, sort by value (descending)

    return valueB - valueA;
  });

/**
 * Format currency value for display
 */
export const formatCurrencyValue = (value: number, currencySymbol = ""): string => {
  if (value <= 0) return "";
  return `${currencySymbol}${value.toLocaleString()}`;
};

export const getButtonLabel = (action_type: any, action_value: any, isUserLoggedIn: any) => {
  if (!isUserLoggedIn && action_type?.trim()?.toLowerCase() === "login") {
    return "Avail";
  }
  if (action_type?.trim()?.toLowerCase() === "install_app" && validations.isUrl(action_value)) {
    return "Download";
  }
  return "View";
};

export default getButtonLabel;
